'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** cn_wall :
//***********************************************************************************
//***********************************************************************************

import { fh_add, fh_matrix, fh_mul, fh_polygon, fh_solid, fh_sub } from '@acenv/fh-3d-viewer';
import { GLAZING_LIST, cn_space, cn_space_segment, code_to_float_color } from '..';
import {
    cn_add,
    cn_dist,
    cn_dot,
    cn_intersect_line,
    cn_md5,
    cn_middle,
    cn_mul,
    cn_normal,
    cn_polar,
    cn_sub,
    cn_translate_polygon,
    cnx_add,
    cnx_clone,
    cnx_mul,
    cnx_normalize,
    cnx_sub
} from '../utils/cn_utilities';
import { cn_element } from './cn_element';
import { cn_opening } from './cn_opening';
import { cn_niche } from './cn_niche';
import { cn_wall_metrics } from './cn_wall_metrics';
import { cn_constant, FRAME_MATERIALS } from './cn_model_constants';
// @ts-ignore

//***********************************************************************************
/**
 * cn_wall_interface : an interface between two spaces, part of a wall.
 * Contains a list of openings that at leat partially match the interface.
 */
//***********************************************************************************
export class cn_wall_interface extends cn_element {
    constructor(wall) {
        super(wall);
        this.class_name = 'cn_wall_interface';
        this.wall = wall;

        /** @type {cn_space_segment[]} */
        this.space_segments = [null, null];

        /** @type {cn_space[]} */
        this.spaces = [null, null];

        /** @type {string[]} */
        this.space_ids = ["", ""];

        /** @type {cn_opening[]} */
        this.openings = [];

        /** @type {cn_niche[]} */
        this.niches = [];

        this.free = false;

        /** @type {number[]} */
        this.wall_positions = [];

        /** @type {number[][]} */
        this.shape = [];

        /** @type {fh_polygon} */
        this.footprint = null;

        this._curtain_wall_data = [];

        /** @type {import("./cn_wall_type.js").cn_wall_type} */
        this.wall_type = null;
        this.flip_wall_type = false;
    }

    serialize() {
        var json = this.serialize_element();
        json.space_ids = this.space_ids;
        json.wall_positions = this.wall_positions;
        json.wall_type = (this.wall_type) ? this.wall_type.ID : "";
        json.flip_wall_type = this.flip_wall_type;
        return json;
    }

    static unserialize(json, wall) {
        let wall_interface = new cn_wall_interface(wall);
        wall_interface.unserialize_element(json);
        if (typeof (json.space_ids) == 'object')
            wall_interface.space_ids = json.space_ids;
        if (typeof (json.wall_positions) == 'object')
            wall_interface.wall_positions = json.wall_positions;
        if (typeof (json.wall_type) == 'string')
            wall_interface.wall_type = wall.parent.building.get_element_type(json.wall_type);
        if (typeof (json.flip_wall_type) == 'boolean')
            wall_interface.flip_wall_type = json.flip_wall_type;
        return wall_interface;
    }

    //***********************************************************************************
    //**** Draw the wall in svg
    //***********************************************************************************
    draw(camera, add_classes = [], fill = '', highlighted_space_segments = []) {
        var html = '';
        
        html += camera.draw_path([this.shape[0], this.shape[1], this.shape[2], this.shape[3]], 'wall', true, fill);

        return html;
    }

    draw_wall_type(camera) {
        var html = '';

        let wall_type = this.get_wall_type();
        let flip_wall_type = this.get_flip_wall_type();

        var w = camera.world_to_screen_scale * wall_type.thickness;
        var h = w * 10;
        html += '<pattern id=\'' + wall_type.ID + camera.ID + '\' x=\'0\' y=\'0\' width=\'' + w + '\' height=\'' + h + '\' patternUnits=\'userSpaceOnUse\' viewBox=\'0 0 ' + w + ' ' + h + '\'>';
        html += wall_type.draw_svg_icon(w, h, true);
        html += '</pattern>';

        var or = this.wall.bounds.pmin;
        var dx = this.wall.bounds.normal;
        let flow_direction = this.get_flow_direction();
        if (flip_wall_type) flow_direction = !flow_direction;
        if (flow_direction) {
            or = this.wall.bounds.pmax;
            dx = cn_mul(this.wall.bounds.normal, -1);
        }
        var ort = camera.world_to_screen(or);
        var dy = this.wall.bounds.direction;

        // @ts-ignore
        function transform(pt) {
            var pt1 = cn_sub(pt, or);
            return [camera.world_to_screen_scale * cn_dot(dx, pt1), camera.world_to_screen_scale * cn_dot(dy, pt1)];
        }

        var angle = cn_polar(this.wall.bounds.direction)[1] * 180 / Math.PI;

        var path = '';
        if (flow_direction)
            path += '<g transform=\'translate(' + ort[0] + ',' + ort[1] + ') rotate(' + (-90 - angle) + ') scale(-1,1)\'>';
        else
            path += '<g transform=\'translate(' + ort[0] + ',' + ort[1] + ') rotate(' + (-90 - angle) + ')\'>';
        //
        path += '<path d=\'';
        var s = transform(this.shape[0]);
        path += ' M ' + s[0] + ' ' + s[1];
        s = transform(this.shape[1]);
        path += ' L ' + s[0] + ' ' + s[1];
        s = transform(this.shape[2]);
        path += ' ' + s[0] + ' ' + s[1];
        s = transform(this.shape[3]);
        path += ' ' + s[0] + ' ' + s[1] + 'Z\' fill=\'url(#' + wall_type.ID + camera.ID + ')\'/>';

        path += '</g>';

        html += path;
        return html;
    }

    /**
     * Copy characteristics (non geometrical) of another Wall Interface
     * @param {cn_wall_interface} other_wall_interface
     */
    copy_parameters(other_wall_interface, copy_id = true) {
        if (copy_id) this.ID = other_wall_interface.ID;
        this.wall_type = other_wall_interface.wall_type;
        this.flip_wall_type = other_wall_interface.flip_wall_type;
    }

    get_wall_type() {
        if (this.parent.wall_interfaces.length < 2)
            return this.parent.wall_type;
        return this.wall_type || this.parent.wall_type;
    }

    get_flip_wall_type() {
        if (this.parent.wall_interfaces.length < 2 || !this.wall_type)
            return this.parent.flip_wall_type;
        return this.flip_wall_type;
    }

    /**
     * returns all wall type used in this wall interface.
     * @returns {import('./cn_wall_type').cn_wall_type[]}
     */
    get_wall_types() {
        if (this.parent.wall_interfaces.length < 2 || this.wall_type == null)
            return this.parent.get_wall_types();
        return [this.wall_type];
    }

    has_wall_type(wall_type) {
        return this.get_wall_types().includes(wall_type);
    }

    get_lowest_slab_height() {
        if (this.wall.is_wall_dormer()) return 0;
        if (this.spaces[0].outside && this.spaces[1].outside) return 0;
        if (this.spaces[0].outside) return this.spaces[1].slab_offset;
        if (this.spaces[1].outside) return this.spaces[0].slab_offset;
        return Math.min(this.spaces[0].slab_offset, this.spaces[1].slab_offset);
    }

    get_highest_slab_height() {
        if (this.wall.is_wall_dormer()) return 0;
        if (this.spaces[0].outside && this.spaces[1].outside) return 0;
        if (this.spaces[0].outside) return this.spaces[1].slab_offset;
        if (this.spaces[1].outside) return this.spaces[0].slab_offset;
        return Math.max(this.spaces[0].slab_offset, this.spaces[1].slab_offset);
    }

    /**
     * returns true if spaces are not heated the same.
     * Always true for wall dormers
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {boolean}
     */
    is_lossy(storey = null) {
        if (this.wall.is_wall_dormer()) return true;
        return this.spaces[0].is_heated(storey) != this.spaces[1].is_heated(storey);
    }

    /**
     * Returns true if this wall goes down over its floor slab.
     * That is the case for curtain walls that are over exterior.
     * @returns {boolean}
     */
    is_over_slab() {
        if (!this.wall.is_curtain_wall()) return false;
        return (this.spaces[0].outside || this.spaces[1].outside);
    }

    /**
     * Returns true if heat flows go from space 0 to space 1, false otherwise.
     * Returns true if not defined
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {boolean}
     */
    get_flow_direction(storey = null) {
        if (this.wall.is_wall_dormer()) return true;

        function space_heating(space) {
            if (space == null) return 0;
            if (space.outside) return 0;
            if (!space.has_roof) return 1;
            if (!space.is_indoor(storey)) return 2;
            if (!space.heated) return 3;
            return 4;
        }

        var s0 = space_heating(this.spaces[0]);
        var s1 = space_heating(this.spaces[1]);
        return s1 <= s0;
    }

    /**
     * returns true if wall can have limit conditions
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {boolean}
     */
    has_limit_conditions(storey = null) {
        if (this.wall.is_wall_dormer()) return false;
        if (!this.is_lossy(storey)) return false;
        if (!this.spaces[0].outside && !this.spaces[1].outside) return false;
        return true;
    }

    /**
     * Builds the polygon corresponding to the side of the wall.
     * @param {number} side
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {fh_polygon}
     */
    build_side_polygon(side, storey) {
        // @ts-ignore
        return this.get_lazy_data(() => {
            let facing_volume = null;
            if (!storey.exterior)
                facing_volume = (this.wall.dormer) ? this.wall.dormer.get_clipping_volume(side) : storey.build_facing_volume();

            var p0 = cnx_clone((side == 0) ? this.shape[0] : this.shape[1]);
            var p1 = cnx_clone((side == 0) ? this.shape[3] : this.shape[2]);

            var nor = cnx_clone(cn_normal(cn_sub(p0, p1)));
            cnx_normalize(nor);

            let zmin = 0;
            let zmax = 0;

            if (storey.exterior) {
                zmax = this.wall.wall_type.wall_height;
            }
            else if (facing_volume) {
                const box = facing_volume.get_bounding_box();
                zmin = box.position[2];
                zmax = box.position[2] + box.size[2];
            }
            var polygon = new fh_polygon(cnx_add(p0, cnx_mul(nor, -0.001)), nor);

            let contour = [];
            p0[2] = zmin;
            contour.push(cnx_clone(p0));
            p0[2] = zmax;
            contour.push(cnx_clone(p0));
            p1[2] = zmax;
            contour.push(cnx_clone(p1));
            p1[2] = zmin;
            contour.push(cnx_clone(p1));
            polygon.add_contour(contour);

            if (facing_volume)
                facing_volume.intersects_polygon(polygon);

            return polygon;
        }, "side_polygon_" + side, storey);
    }

    /**
     * Builds the 3D shape of the wall
     * @param {import('./cn_storey').cn_storey} storey
     * @param {boolean} remove_openings
     * @param {number} upper_part : index of the upper part.
     * @returns {fh_solid}
     */
    build_3d_solid(storey, remove_openings = true, upper_part = 0) {
        if (storey.exterior) return null;

        if (upper_part > this.wall.upper_wall_types.length) return null;

        // @ts-ignore
        return this.get_lazy_data(() => {
            var solid_wall = new fh_solid();

            let roof_volume = null;
            if (!storey.exterior)
                roof_volume = (this.wall.dormer) ? this.wall.dormer.get_clipping_volume(0) : storey.build_space_volume();

            let zmin = this.get_lowest_slab_height();
            let zmax = 0;

            if (storey.exterior) {
                zmax = this.wall.wall_type.wall_height;
            }
            else if (roof_volume) {
                const box = roof_volume.get_bounding_box();
                zmax = box.position[2] + box.size[2];
            }

            let upper_walls = this.wall.get_dummy_walls();
            if (upper_walls.length == 1) {
                var footprint = this.footprint.clone();
                cn_translate_polygon(footprint, [0, 0, zmin]);
                solid_wall.extrusion(footprint, [0, 0, zmax - zmin]);
            }
            else {
                let index = this.wall.wall_interfaces.indexOf(this);
                let border0 = (index == 0);
                let border1 = (index == this.wall.wall_interfaces.length - 1);
                upper_walls.filter(wall => wall["_nu"] == upper_part).forEach(wall => {
                    let z0 = Math.max(zmin, wall["_z0"]);
                    let z1 = Math.min(zmax, wall["_z1"]);
                    if (z1 - z0 < 0.001) return;
                    let ctr = [];
                    ctr.push((border0) ? wall.shape[0] : wall.compute_side_position_from_abcissa(this.wall_positions[0], 0));
                    ctr.push((border0) ? wall.shape[1] : wall.compute_side_position_from_abcissa(this.wall_positions[0], 1));
                    ctr.push((border1) ? wall.shape[2] : wall.compute_side_position_from_abcissa(this.wall_positions[1], 1));
                    ctr.push((border1) ? wall.shape[3] : wall.compute_side_position_from_abcissa(this.wall_positions[1], 0));
                    let contour = ctr.map(v => cnx_clone(v));
                    let footprint = new fh_polygon([0, 0, z0], [0, 0, 1]);
                    footprint.add_contour(contour);
                    let solid = new fh_solid();
                    solid.extrusion(footprint, [0, 0, z1 - z0]);
                    solid_wall.unites(solid);
                });
            }

            //*** Add openings
            if (remove_openings && this.openings.length > 0) {
                //*** data for hole geometry
                var dx = [this.wall.bounds.direction[0], this.wall.bounds.direction[1], 0];
                var dy = [-this.wall.bounds.direction[1], this.wall.bounds.direction[0], 0];
                var dz = [0, 0, 1];
                var p0 = [this.wall.bounds.pmin[0], this.wall.bounds.pmin[1], 0];
                p0 = fh_sub(p0, fh_mul(dy, 0.01));

                //*** loop on openings
                this.openings.filter(op => op.valid).forEach(opening => {
                    p0[2] = opening.compute_offset();
                    //*** add opening hole
                    var opening_solid = opening.get_opening_type().build_piercing_solid(fh_add(p0, fh_mul(dx, opening.position)), dx, dy, dz, this.wall.wall_type.thickness + 0.02);
                    solid_wall.substracts(opening_solid);
                });
            }

            //*** Add openings
            if (remove_openings && this.niches.length > 0) {
                this.niches.forEach(niche => {
                    let solid = niche.build_piercing_solid();
                    if (solid) solid_wall.substracts(solid);
                });
            }

            solid_wall.intersects(roof_volume);

            //*** add the volume of the slab if curtain wall has one space outside */
            if (this.is_over_slab()) {
                var box = solid_wall.get_bounding_box();
                var z0 = box.position[2];
                const pg = this.footprint.clone();
                cn_translate_polygon(pg, [0, 0, z0 + 0.001]);
                const space = this.spaces.find(sp => !sp.outside);
                var max_thickness = 0;
                if (space) {
                    storey.slabs.filter(s => s.spaces[1] == space).forEach(s => {
                        if (s.slab_type.thickness > max_thickness) max_thickness = s.slab_type.thickness;
                    });
                } else
                    max_thickness = 0.3;
                const added_vol = new fh_solid();
                added_vol.extrusion(pg, [0, 0, -max_thickness - 0.001]);
                solid_wall.unites(added_vol);
            }

            return solid_wall;
        }, (remove_openings) ? "solid_pierced" + upper_part : "solid_unpierced" + upper_part, storey);
    }

    /**
     * Builds the 3D shape of the wall
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {fh_solid[]}
     */
    build_3d_solids(storey) {
        // @ts-ignore
        return this.get_lazy_data(() => {
            if (!this.wall.is_curtain_wall()) {
                let solid_walls = [];
                for (var n = 0; n <= this.wall.upper_wall_types.length; n++)
                    solid_walls.push(this.build_3d_solid(storey, true, n));
                return solid_walls;
            }
            var solid_wall = this.build_3d_solid(storey, true);
            return this._build_curtain_wall(storey, solid_wall);
        }, "3d_solids", storey);
    }

    /**
     *
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {fh_polygon}
     */
    _get_filling_polygon(storey) {
        // @ts-ignore
        return this.get_lazy_data(() => {
            const wall_volume = this.build_3d_solid(storey, true);
            var box = wall_volume.get_bounding_box();
            var z0 = box.position[2];
            var z1 = box.position[2] + box.size[2];
            const filling_polygon = new fh_polygon(cnx_clone(cn_middle(this.wall.bounds.pmin, this.wall.bounds.pmax)), cnx_clone(this.wall.bounds.normal));
            var pw0 = this.wall.vertex_position(0);
            var pw1 = this.wall.vertex_position(1);
            filling_polygon.add_contour([
                cnx_clone(pw0, z0),
                cnx_clone(pw1, z0),
                cnx_clone(pw1, z1),
                cnx_clone(pw0, z1)
            ]);
            this.wall.remove_openings_from_polygon(filling_polygon, storey);
            filling_polygon.compute_contours();
            wall_volume.intersects_polygon(filling_polygon);
            filling_polygon.offset(-0.5 * this.wall.wall_type.thickness);
            const normal = cnx_clone(this.wall.bounds.normal);
            filling_polygon.project(cnx_clone(this.shape[0], z0), normal, normal);
            return filling_polygon;
        }, "filling_polygon", storey);
    }

    _build_curtain_wall(storey, wall_volume) {
        var solid_walls = [];

        //*** add the volume of the slab if curtain wall has one space outside */
        var box = wall_volume.get_bounding_box();
        var z0 = box.position[2];
        var z1 = box.size[2] + box.position[2];

        const wall_type = this.wall.wall_type;
        const thickness = wall_type.thickness;
        const normal = cnx_clone(this.wall.bounds.normal);
        const direction = cnx_clone(this.wall.bounds.direction);
        const filling_polygon = this._get_filling_polygon(storey);
        const thickness_normal = cnx_mul(normal, thickness);

        const frame_color = cn_constant.code_to_float_color(wall_type.frame, FRAME_MATERIALS);

        var opaque_thickness = wall_type.thickness * 0.9;
        var glass_thickness = 0.006;
        if (wall_type.glazing == 'double') glass_thickness = 0.022;
        else if (wall_type.glazing == 'triple') glass_thickness = 0.040;
        else if (wall_type.glazing == 'none') glass_thickness = thickness * 0.9;
        const glazing_color = (wall_type.glazing == 'none') ? frame_color : code_to_float_color(wall_type.glazing, GLAZING_LIST);

        function _add_mullion(m) {
            m.intersects(filling_polygon);
            if (m.get_area() > 0) {
                var solid = new fh_solid();
                solid.extrusion(m, thickness_normal);
                solid.color = frame_color;
                if (solid.get_faces().length) solid_walls.push(solid);
            }
        }

        function _add_panel(pg, glazing) {
            pg.intersects(filling_polygon);
            if (pg.get_area() > 0) {
                const thickness = (glazing) ? glass_thickness : opaque_thickness;
                var panel = new fh_solid();
                panel.extrusion(pg, cnx_mul(normal, thickness));
                panel.color = (glazing) ? glazing_color : frame_color;
                if (panel.get_faces().length) solid_walls.push(panel);
            }
        }

        //*** vertical mullions */
        let nb = 0;
        let panel_width = wall_type.panel_width;

        if (wall_type.adapt_panels) {
            nb = Math.round(this.wall.bounds.length / wall_type.panel_width);
            if (nb == 0) nb = 1;
            panel_width = this.wall.bounds.length / nb;
        }
        else {
            nb = Math.ceil(this.wall.bounds.length / wall_type.panel_width);
            if (nb == 0) nb = 1;
        }
        const p0 = cnx_clone(this.wall.vertex_position(0), z0);
        const p1 = cnx_clone(this.wall.vertex_position(0), z1);
        const hmullion = cnx_mul(direction, panel_width - wall_type.thickness);

        const mullion_ref = cnx_clone(this.shape[0]);
        const glazing_ref = cnx_add(mullion_ref, cnx_mul(this.wall.bounds.normal, 0.5 * wall_type.thickness - 0.5 * glass_thickness));
        const opaque_ref = cnx_add(mullion_ref, cnx_mul(this.wall.bounds.normal, 0.5 * wall_type.thickness - 0.5 * opaque_thickness));

        const mullion_heights = [z0, wall_type.panel_heights[0], wall_type.panel_heights[0] + wall_type.panel_heights[1], z1];
        for (var n = 0; n < nb; n++) {
            const x0 = (n == 0) ? -2 * wall_type.thickness : n * panel_width - 0.5 * wall_type.thickness;
            var x1 = n * panel_width + 0.5 * wall_type.thickness;
            if (nb == n) x1 = this.wall.bounds.length + wall_type.thickness * 1.5;

            //*** vertical mullion */
            if (n > 0) {
                var pg = new fh_polygon(mullion_ref, normal);
                var contour = [];
                contour.push(cnx_add(p0, cnx_mul(direction, x0)));
                contour.push(cnx_add(p0, cnx_mul(direction, x1)));
                contour.push(cnx_add(p1, cnx_mul(direction, x1)));
                contour.push(cnx_add(p1, cnx_mul(direction, x0)));
                pg.add_contour(contour);
                _add_mullion(pg);
            }

            if (n == nb) break;

            var z = z0;
            const pp0 = cnx_add(p0, cnx_mul(direction, x1));
            const pp1 = cnx_add(pp0, hmullion);
            for (var h = 0; h < 3; h++) {
                const z = mullion_heights[h];
                //*** horizontal mullion */
                if (h > 0) {
                    var zz0 = z - 0.5 * wall_type.thickness;
                    var zz1 = zz0 + wall_type.thickness;
                    var pg = new fh_polygon(mullion_ref, normal);
                    var contour = [];
                    contour.push([pp0[0], pp0[1], zz0]);
                    contour.push([pp1[0], pp1[1], zz0]);
                    contour.push([pp1[0], pp1[1], zz1]);
                    contour.push([pp0[0], pp0[1], zz1]);
                    pg.add_contour(contour);
                    _add_mullion(pg);
                }

                var znext = mullion_heights[h + 1];

                //*** panel */
                const glazing = (wall_type.parities[h] == 'glazed' || (wall_type.parities[h] == 'partial' && ((n % wall_type.panels) != 0)));
                zz0 = z + 0.5 * wall_type.thickness;
                zz1 = znext - 0.5 * wall_type.thickness;
                pg = new fh_polygon((glazing) ? glazing_ref : opaque_ref, normal);
                contour = [];
                contour.push([pp0[0], pp0[1], zz0]);
                contour.push([pp1[0], pp1[1], zz0]);
                contour.push([pp1[0], pp1[1], zz1]);
                contour.push([pp0[0], pp0[1], zz1]);
                pg.add_contour(contour);
                _add_panel(pg, glazing);
            }
        }

        //*** add frame
        const fp = filling_polygon.clone();
        var pt = fp.get_point();
        pt = cnx_sub(pt, cnx_mul(thickness_normal, 0.5))
        fp.project(pt, normal, normal);
        const frame_hole = new fh_solid();
        frame_hole.extrusion(fp, cnx_mul(thickness_normal, 2));
        wall_volume.substracts(frame_hole);
        wall_volume.color = frame_color;
        if (wall_volume.get_faces().length) solid_walls.push(wall_volume);

        return solid_walls;
    }

    /**
     * Returns the list of opening polygons
     * @param {number} z
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {Array<fh_polygon>}
     */
    get_curtain_wall_openings(z, storey) {
        if (!this.wall.is_curtain_wall()) return [];

        const filling_polygon = this._get_filling_polygon(storey);
        const wall_type = this.wall.wall_type;
        const thickness = wall_type.thickness;

        let nb = 0;
        let panel_width = this.wall.wall_type.panel_width;

        if (wall_type.adapt_panels) {
            nb = Math.round(this.wall.bounds.length / this.wall.wall_type.panel_width);
            if (nb == 0) nb = 1;
            panel_width = this.wall.bounds.length / nb;
        }
        else {
            nb = Math.ceil(this.wall.bounds.length / wall_type.panel_width);
            if (nb == 0) nb = 1;
        }
        const p0 = cnx_clone(this.wall.vertex_position(0), 0);
        const direction = cnx_clone(this.wall.bounds.direction);

        const mullion_heights = [0, this.wall.wall_type.panel_heights[0], this.wall.wall_type.panel_heights[0] + this.wall.wall_type.panel_heights[1], 20];

        const normal = cnx_clone(this.wall.bounds.normal);

        const matrix = new fh_matrix();
        matrix.load_translation([0, 0, z]);

        const opening_polygons = [];
        for (var n = 0; n < nb; n++) {
            var x0 = n * panel_width + 0.5 * thickness;
            var x1 = (n == nb - 1) ? this.wall.bounds.length - thickness * 0.5 : x0 + panel_width - thickness;

            const pp0 = cnx_add(p0, cnx_mul(direction, x0));
            const pp1 = cnx_add(p0, cnx_mul(direction, x1));
            for (var h = 0; h < 3; h++) {
                const z = mullion_heights[h];
                var znext = mullion_heights[h + 1];

                //*** panel */
                if (wall_type.parities[h] == 'glazed' || (wall_type.parities[h] == 'partial' && ((n % wall_type.panels) != 0))) {
                    var zz0 = z + 0.5 * wall_type.thickness;
                    var zz1 = znext - 0.5 * wall_type.thickness;
                    var pg = new fh_polygon(pp0, normal);
                    var contour = [];
                    contour.push([pp0[0], pp0[1], zz0]);
                    contour.push([pp1[0], pp1[1], zz0]);
                    contour.push([pp1[0], pp1[1], zz1]);
                    contour.push([pp0[0], pp0[1], zz1]);
                    pg.add_contour(contour);
                    pg.intersects(filling_polygon);
                    if (pg.get_area() > 0.1) {
                        pg.apply_matrix(matrix);
                        opening_polygons.push(pg);
                    }
                }
            }
        }
        return opening_polygons;
    }

    /**
     * Returns the length of the side
     * @param {number} side
     * @returns {number}
     */
    get_length(side) {
        if (side == 0) return cn_dist(this.shape[0], this.shape[3]);
        return cn_dist(this.shape[1], this.shape[2]);
    }

    /**
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {cn_wall_metrics}
     */
    get_metrics(storey) {
        // @ts-ignore
        return this.get_lazy_data(() => {
            return new cn_wall_metrics(this, storey);
        }, "metrics", storey);
    }
}
